/**
 * @fileoverview The three-dot actions menu for a tenant's table row.
 * @description Shared by every sales table that lists banks (Sales Pipeline, Active Clients, My
 * Page, My Team), so a record offers the same actions wherever it appears. Lists View first, then
 * every action the viewer's role allows on this record (see resolveTenantActions) — View opens the
 * read-only detail view (TenantDetailModal with `readOnly`), and every other entry opens that
 * action's form on its own (see TenantActionModal). A page whose real backend now serves some of
 * these actions (Sales Pipeline: edit, archive, restore) hides the mock-only entries with `omit`
 * and adds its own with `extraActions`, placed right after View.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useTranslation } from 'react-i18next'

import {
  classifyTenantAccess,
  resolveTenantActions,
  TENANT_ACTION_PERMISSION,
} from '@/features/sales/lib/tenant-access.utils'
import RowActionsMenu from '@/shared/components/molecules/RowActionsMenu'

import type {
  TenantAction,
  TenantActionPermissions,
} from '@/features/sales/lib/tenant-access.utils'
import type { Target, SalesUserRecord, Tenant } from '@/features/sales/types/tenant.types'
import type { RowAction } from '@/shared/components/molecules/RowActionsMenu'
import type { TFunction } from 'i18next'

// The menu's actions after View, in display order, each labelled under
// `sales.tenantDetail.actions`. Archive is the one destructive entry, so it's last.
const ROW_ACTIONS: TenantAction[] = [
  'edit',
  'editTarget',
  'updateStage',
  'scheduleFollowUp',
  'addActivity',
  'reassign',
  'confirmPayment',
  'restore',
  'archive',
]

/**
 * @description Builds the row's menu: View first, then every action the viewer may take.
 * @param {TFunction} t - The translate function.
 * @param {TenantActionPermissions} permissions - What the viewer may do with this tenant.
 * @param {() => void} onView - Opens the read-only detail view.
 * @param {(action: TenantAction) => void} onAction - Opens one action's form.
 * @param {TenantAction[]} omit - Actions to leave out of the menu.
 * @param {RowAction[]} extraActions - Entries to add right after View.
 * @returns {RowAction[]} The menu items.
 */
function buildTenantRowActions(
  t: TFunction,
  permissions: TenantActionPermissions,
  onView: () => void,
  onAction: (action: TenantAction) => void,
  omit: TenantAction[],
  extraActions: RowAction[],
): RowAction[] {
  return [
    { key: 'view', label: t('sales.activeClients.view'), onSelect: onView },
    ...extraActions,
    ...ROW_ACTIONS.filter(
      (action) => !omit.includes(action) && permissions[TENANT_ACTION_PERMISSION[action]],
    ).map((action): RowAction => ({
      key: action,
      label: t(`sales.tenantDetail.actions.${action}`),
      onSelect: () => onAction(action),
      tone: action === 'archive' ? 'danger' : 'default',
    })),
  ]
}

interface TenantRowActionsProps {
  tenant: Tenant
  /** The tenant's relevant target (see pickRelevantTarget), when the page has loaded targets. */
  target: Pick<Target, 'status'> | undefined
  roster: SalesUserRecord[]
  viewerUserId: string
  viewerRole: string
  onView: () => void
  onAction: (action: TenantAction) => void
  /** Actions to leave out of the menu (default none). */
  omit?: TenantAction[] | undefined
  /** Entries to add right after View (default none). */
  extraActions?: RowAction[] | undefined
}

const NO_ACTIONS: never[] = []

/**
 * @component
 * @param {TenantRowActionsProps} props - The record, the viewer, and what each item opens.
 * @param {Tenant} props.tenant - The row's tenant.
 * @param {Pick<Target, 'status'> | undefined} props.target - Its relevant target, if known.
 * @param {SalesUserRecord[]} props.roster - The sales roster, for hierarchy scope.
 * @param {string} props.viewerUserId - The signed-in user's sales-roster id.
 * @param {string} props.viewerRole - The signed-in user's active role name.
 * @param {() => void} props.onView - Opens the read-only detail view.
 * @param {(action: TenantAction) => void} props.onAction - Opens one action's form.
 * @param {TenantAction[]} [props.omit] - Actions to leave out.
 * @param {RowAction[]} [props.extraActions] - Entries to add after View.
 */
function TenantRowActions({
  tenant,
  target,
  roster,
  viewerUserId,
  viewerRole,
  onView,
  onAction,
  omit = NO_ACTIONS,
  extraActions = NO_ACTIONS,
}: TenantRowActionsProps) {
  const { t } = useTranslation()
  const access = classifyTenantAccess(tenant, viewerUserId, viewerRole, roster)
  const permissions = resolveTenantActions(tenant, target, access)

  return (
    <RowActionsMenu
      label={t('sales.pipeline.rowActions', { name: tenant.name })}
      actions={buildTenantRowActions(t, permissions, onView, onAction, omit, extraActions)}
    />
  )
}

export default TenantRowActions
